import { ReactNode, useMemo } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  Area,
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Divider,
  Stack,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import {
  ChartMetricsRowSkeleton,
  ChartMetricSkeleton,
} from '@cast/features-cluster-ui';
import { Money } from '@cast/split-me/components';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { attachTimeTicks } from '@cast/split-me/utils';
import { dashesOrNum } from '@cast/utils';

import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';

import { TimeSeriesWithTicks } from '@cast/split-me/types';

dayjs.extend(utc);

type StackedAreaChartValueType = 'currency' | 'number';

type StackedAreaChartMetricSettings<K extends string> = Record<
  K,
  {
    color?: string;
    title: string;
    tooltipTitle?: string;
  }
>;

type StackedAreaChartDataItem = {
  timestamp: string;
  chartTimestampUnix: number;
};

type StackedAreaChartProps<
  T extends StackedAreaChartDataItem,
  K extends string
> = {
  title: string;
  testId: string;
  data: T[];
  totals: Record<K, number>;
  metricSettings: StackedAreaChartMetricSettings<K>;
  metricKeys: K[];
  chartMetricKeys: K[];
  timeZone: string;
  unit: string;
  valueType: StackedAreaChartValueType;
  yAxisFormatter?: (value: number) => string;
  metricFormatter?: (value: number) => { value: number; unit?: ReactNode };
  tooltipFormatter?: (value: number) => ReactNode;
  isLoading: boolean;
  error: Error | null | undefined;
  refetch: () => void;
};

const getCurrencyPrecision = (amount: number) => (amount < 1 ? 6 : 2);

export const StackedAreaChart = <
  T extends StackedAreaChartDataItem,
  K extends string
>({
  title,
  testId,
  data,
  totals,
  metricSettings,
  metricKeys,
  chartMetricKeys,
  timeZone,
  unit,
  valueType,
  yAxisFormatter,
  metricFormatter,
  tooltipFormatter,
  isLoading,
  error,
  refetch,
}: StackedAreaChartProps<T, K>) => {
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<K>();

  const chartData = useMemo(() => attachTimeTicks(data), [data]);

  const hasNoData = !chartData.length;

  const chartMetricKeySet = new Set(chartMetricKeys);

  const colorMap = metricKeys.reduce((acc, key) => {
    acc[key] = metricSettings[key].color;
    return acc;
  }, {} as Record<K, string | undefined>);

  const titleMap = metricKeys.reduce((acc, key) => {
    acc[key] = metricSettings[key].title;
    return acc;
  }, {} as Record<K, string>);

  const formatYAxisValue =
    yAxisFormatter ??
    ((value: number) =>
      valueType === 'currency' ? `$ ${value}` : `${value} ${unit}`);

  const formatTooltipValue = (value: string | number) => {
    const amount = Number(value);

    if (tooltipFormatter) {
      return tooltipFormatter(amount);
    }

    if (valueType === 'currency') {
      const precision = getCurrencyPrecision(amount);
      return <Money amount={amount} precision={precision} fixed={precision} />;
    }

    return (
      <>{`${dashesOrNum({
        value: amount,
        roundBy: 3,
        decimals: 1,
      })} ${unit}`}</>
    );
  };

  return (
    <WidgetLayout title={title} testId={testId}>
      {error ? (
        <FailedToLoad
          title={`Failed to load ${title.toLowerCase()}`}
          refresh={refetch}
        />
      ) : (
        <Stack gap={16} height="100%">
          {isLoading ? (
            <ChartMetricsRowSkeleton
              alwaysRow
              gap={16}
              testId={`${testId}-metrics-skeleton`}
            >
              {metricKeys.map((key) => (
                <ChartMetricSkeleton key={key} />
              ))}
            </ChartMetricsRowSkeleton>
          ) : (
            <Stack direction="row" gap={16} alignItems="center" flexWrap="wrap">
              {metricKeys.map((key, index) => {
                const formattedMetric = metricFormatter?.(totals[key]);
                const metricValue = formattedMetric?.value ?? totals[key];
                const metricUnit =
                  formattedMetric?.unit ??
                  (valueType === 'number' ? unit : undefined);

                return (
                  <Stack key={key} direction="row" gap={16} alignItems="center">
                    {index > 0 && <Divider orientation="vertical" flexItem />}
                    <InventoryResourceMetricBox
                      testId={`${testId}-${key}`}
                      dataKey={key}
                      size="m"
                      value={metricValue}
                      type={valueType}
                      precision={
                        valueType === 'currency'
                          ? getCurrencyPrecision(metricValue)
                          : undefined
                      }
                      fixed={
                        valueType === 'currency'
                          ? getCurrencyPrecision(metricValue)
                          : undefined
                      }
                      unit={metricUnit}
                      colorMap={colorMap}
                      titleMap={titleMap}
                      toggleMetric={
                        chartMetricKeySet.has(key) ? toggleMetric : undefined
                      }
                      isMetricDisabled={
                        chartMetricKeySet.has(key)
                          ? isMetricDisabled
                          : undefined
                      }
                    />
                  </Stack>
                );
              })}
            </Stack>
          )}
          <Box flex={1} minHeight={0}>
            <ChartContainer<TimeSeriesWithTicks<T>[number]>
              data={chartData}
              isLoading={isLoading}
              hasNoData={hasNoData}
              sx={{ height: '100%' }}
            >
              {chartMetricKeys.map((key) =>
                !isMetricDisabled(key) ? (
                  <Area<TimeSeriesWithTicks<T>[number]>
                    key={key}
                    dataKey={key}
                    stackId={testId}
                    fill={metricSettings[key].color}
                    tooltipConfig={{
                      displayName:
                        metricSettings[key].tooltipTitle ??
                        metricSettings[key].title,
                    }}
                  />
                ) : null
              )}
              <ChartGrid hideVertical />
              <TimeXAxis<TimeSeriesWithTicks<T>[number]>
                unixDataKey="chartTimestampUnix"
                timezone={timeZone}
              />
              <YAxis
                tickFormatter={formatYAxisValue}
                allowDecimals
                domain={[0, 'dataMax']}
              />
              <ChartTooltip<TimeSeriesWithTicks<T>[number]>
                header={(headerProps) => (
                  <ChartTooltipTimestampHeader
                    {...headerProps}
                    timezone={timeZone}
                  />
                )}
                formatValue={formatTooltipValue}
              />
            </ChartContainer>
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
